import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Autocomplete,
  TextField,
  Box,
  Typography,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Divider,
  Link,
} from '@mui/material';
import { apiService } from '../services/apiService';

// Mirror the legacy admin "active project" predicate used in
// ProjectsPane: a project is selectable as a move target only if it's
// not deactivated and its lifecycle status is still active. Archived
// or completed projects shouldn't accept new attribution.
const projectIsActive = (p) => p?.is_active !== false && (!p?.status || p.status === 'active');

const ownerLabelOf = (p) => {
  if (p?.owner_type === 'organization') {
    const o = p.owner_id || p.owner || {};
    return o.name || o.short_name || '';
  }
  const o = p?.owner_id || p?.owner || {};
  const fullName = [o.first_name, o.last_name].filter(Boolean).join(' ').trim();
  return fullName || o.name || o.username || o.email || '';
};

/**
 * Move one or many observations to a different project.
 *
 * Two modes:
 *  - Single: caller passes `observation` (full doc). Dialog hides the
 *    current project from the target list.
 *  - Bulk:   caller passes `observationIds` (string array). Dialog
 *    confirms by fanning out per-id calls via Promise.allSettled and
 *    summarises any partial failures.
 */
const MoveObservationDialog = ({
  open,
  onClose,
  observation = null,
  observationIds = null,
  onMoved,
}) => {
  const isBulk = Array.isArray(observationIds) && observationIds.length > 0;
  const idsToMove = isBulk
    ? observationIds
    : (observation ? [observation._id || observation.id] : []);

  const [projects, setProjects] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [target, setTarget] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [partialFailures, setPartialFailures] = useState(null);

  // Inline create-new-project form
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createName, setCreateName] = useState('');
  const [createCode, setCreateCode] = useState('');
  const [createDescription, setCreateDescription] = useState('');
  const [createType, setCreateType] = useState('research');

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      setLoadingProjects(true);
      setError(null);
      try {
        const r = await apiService.getAllProjects();
        const list = Array.isArray(r) ? r : [];
        if (!cancelled) setProjects(list);
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.error?.message || e.message || 'Failed to load projects');
      } finally {
        if (!cancelled) setLoadingProjects(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open]);

  // Reset transient state when the dialog opens. The deps deliberately
  // exclude observation/observationIds — those props' identities change
  // on every parent render (e.g. observationIds={Array.from(selectedIds)}
  // from OperationsDashboard creates a fresh array each time), so
  // including them clears the user's just-picked target every time the
  // parent re-renders. Resetting on `open` toggle is the right scope:
  // close → next open is what users see as "starting fresh".
  useEffect(() => {
    if (!open) return;
    setTarget(null);
    setError(null);
    setPartialFailures(null);
    setShowCreate(false);
    setCreateName('');
    setCreateCode('');
    setCreateDescription('');
    setCreateType('research');
  }, [open]);

  const currentProjectId = useMemo(() => {
    if (isBulk) return null; // multiple sources possible
    const pid = observation?.project_id;
    return pid?._id || pid?.id || pid || null;
  }, [observation, isBulk]);

  const targetOptions = useMemo(() => {
    return projects
      .filter(projectIsActive)
      .filter((p) => {
        if (!currentProjectId) return true;
        const id = p._id || p.id;
        return String(id) !== String(currentProjectId);
      });
  }, [projects, currentProjectId]);

  const handleCreate = async () => {
    if (!createName.trim() || !createCode.trim() || !createDescription.trim()) return;
    setCreating(true);
    setError(null);
    try {
      const r = await apiService.createProject({
        name: createName.trim(),
        code: createCode.trim(),
        description: createDescription.trim(),
        type: createType,
        // Owner_type/owner_id intentionally omitted — server defaults
        // to user-owned for the calling user, which is what we want
        // for the "create on the fly" path.
      });
      const created = r?.data?.project || r?.data || null;
      if (created) {
        setProjects((prev) => [created, ...prev]);
        setTarget(created);
      }
      setShowCreate(false);
      setCreateName('');
      setCreateCode('');
      setCreateDescription('');
    } catch (e) {
      setError(e.response?.data?.error?.message || e.message || 'Failed to create project');
    } finally {
      setCreating(false);
    }
  };

  const handleConfirm = async () => {
    if (!target || idsToMove.length === 0) return;
    setSubmitting(true);
    setError(null);
    setPartialFailures(null);

    const targetId = target._id || target.id;

    try {
      if (!isBulk) {
        const r = await apiService.moveObservationToProject(idsToMove[0], targetId);
        try {
          window.postMessage({
            type: 'observationMoved',
            observationId: idsToMove[0],
            fromProjectId: currentProjectId,
            toProjectId: targetId,
          }, '*');
        } catch (_) { /* */ }
        if (onMoved) onMoved({ moved: [idsToMove[0]], failed: [], observation: r?.data?.observation });
        onClose?.();
      } else {
        const settled = await Promise.allSettled(
          idsToMove.map((id) => apiService.moveObservationToProject(id, targetId))
        );
        const moved = [];
        const failed = [];
        settled.forEach((res, idx) => {
          const id = idsToMove[idx];
          if (res.status === 'fulfilled') {
            moved.push(id);
            try {
              window.postMessage({
                type: 'observationMoved',
                observationId: id,
                fromProjectId: null,
                toProjectId: targetId,
              }, '*');
            } catch (_) { /* */ }
          } else {
            const reason = res.reason?.response?.data?.error?.message
              || res.reason?.message
              || 'Move failed';
            failed.push({ id, reason });
          }
        });
        if (failed.length === 0) {
          if (onMoved) onMoved({ moved, failed });
          onClose?.();
        } else {
          setPartialFailures({ moved, failed });
          if (onMoved) onMoved({ moved, failed });
        }
      }
    } catch (e) {
      setError(e.response?.data?.error?.message || e.message || 'Move failed');
    } finally {
      setSubmitting(false);
    }
  };

  const title = isBulk
    ? `Move ${idsToMove.length} observations to project`
    : 'Move observation to project';

  return (
    <Dialog open={!!open} onClose={() => !submitting && onClose?.()} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontSize: '0.95rem' }}>{title}</DialogTitle>
      <DialogContent dividers>
        {!isBulk && observation && (
          <Box sx={{ mb: 1.5 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              Currently in project:{' '}
              <strong>
                {observation.project_id?.code || observation.project_id?.name || '(no project)'}
              </strong>
            </Typography>
          </Box>
        )}

        {loadingProjects ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <Autocomplete
            options={targetOptions}
            value={target}
            onChange={(_, v) => setTarget(v)}
            disabled={submitting}
            getOptionLabel={(p) => {
              const code = p.code ? `${p.code} — ` : '';
              const owner = ownerLabelOf(p);
              return `${code}${p.name}${owner ? ` (${owner})` : ''}`;
            }}
            isOptionEqualToValue={(a, b) => String(a._id || a.id) === String(b._id || b.id)}
            renderInput={(params) => (
              <TextField {...params} label="Move to project" size="small" autoFocus />
            )}
          />
        )}

        <Box sx={{ mt: 1 }}>
          {!showCreate ? (
            <Link
              component="button"
              type="button"
              onClick={() => setShowCreate(true)}
              disabled={submitting}
              sx={{ fontSize: '0.75rem' }}
            >
              + Create a new project
            </Link>
          ) : (
            <Box sx={{ mt: 1, p: 1.5, border: '1px solid rgba(255,255,255,0.1)', borderRadius: 1 }}>
              <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 1 }}>
                Create new project
              </Typography>
              <Stack spacing={1}>
                <TextField
                  size="small"
                  label="Name"
                  value={createName}
                  onChange={(e) => setCreateName(e.target.value)}
                  disabled={creating}
                />
                <TextField
                  size="small"
                  label="Code"
                  value={createCode}
                  onChange={(e) => setCreateCode(e.target.value.toUpperCase())}
                  disabled={creating}
                  inputProps={{ maxLength: 16 }}
                />
                <TextField
                  size="small"
                  label="Description"
                  value={createDescription}
                  onChange={(e) => setCreateDescription(e.target.value)}
                  disabled={creating}
                  multiline
                  minRows={2}
                  maxRows={4}
                  inputProps={{ maxLength: 1000 }}
                />
                <FormControl size="small">
                  <InputLabel>Type</InputLabel>
                  <Select
                    value={createType}
                    label="Type"
                    onChange={(e) => setCreateType(e.target.value)}
                    disabled={creating}
                  >
                    <MenuItem value="research">Research</MenuItem>
                    <MenuItem value="education">Education</MenuItem>
                    <MenuItem value="outreach">Outreach</MenuItem>
                    <MenuItem value="personal">Personal</MenuItem>
                    <MenuItem value="testing">Testing</MenuItem>
                  </Select>
                </FormControl>
                <Stack direction="row" spacing={1} sx={{ justifyContent: 'flex-end' }}>
                  <Button size="small" onClick={() => setShowCreate(false)} disabled={creating}>
                    Cancel
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={handleCreate}
                    disabled={creating || !createName.trim() || !createCode.trim() || !createDescription.trim()}
                  >
                    {creating ? 'Creating…' : 'Create & select'}
                  </Button>
                </Stack>
              </Stack>
            </Box>
          )}
        </Box>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>
        )}

        {partialFailures && partialFailures.failed.length > 0 && (
          <>
            <Divider sx={{ my: 2 }} />
            <Alert severity="warning">
              Moved {partialFailures.moved.length} of {idsToMove.length}.{' '}
              {partialFailures.failed.length} failed:
              <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                {partialFailures.failed.map((f) => (
                  <li key={f.id} style={{ fontSize: '0.75rem' }}>
                    <code>{f.id}</code>: {f.reason}
                  </li>
                ))}
              </Box>
            </Alert>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose?.()} disabled={submitting}>
          {partialFailures ? 'Close' : 'Cancel'}
        </Button>
        {!partialFailures && (
          <Button
            variant="contained"
            onClick={handleConfirm}
            disabled={submitting || !target || idsToMove.length === 0}
          >
            {submitting ? 'Moving…' : (isBulk ? `Move ${idsToMove.length}` : 'Move')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default MoveObservationDialog;
